import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Star, MapPin, Briefcase, ArrowLeft, Clock } from "lucide-react";
import api from "../lib/apiClient";
import { AuthImage } from "../components/AuthImage";
import { toast } from "sonner";

export default function ProfessionalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const load = () => api.get(`/marketplace/professionals/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submitReview = async () => {
    try { await api.post(`/marketplace/professionals/${id}/reviews`, { rating, comment }); toast.success("Avis publié"); setComment(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  if (p === null) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;
  if (p === false) return <div className="text-center py-20 text-zinc-500" data-testid="pro-not-found">Profil introuvable ou non disponible.</div>;

  return (
    <div className="space-y-6" data-testid="pro-detail">
      <button onClick={() => navigate("/marketplace")} className="text-sm text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Retour au marché</button>

      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-extrabold" data-testid="pro-name">{p.name || "Professionnel"}</h1>
            {p.headline && <p className="text-zinc-300 mt-1">{p.headline}</p>}
            <div className="flex items-center gap-3 mt-2 text-sm text-zinc-500">
              {(p.city || p.country) && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{[p.city, p.country].filter(Boolean).join(", ")}</span>}
              {p.experience_years != null && <span className="inline-flex items-center gap-1"><Briefcase className="w-4 h-4" />{p.experience_years} an(s) d'exp.</span>}
              {p.availability && <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" />{p.availability}</span>}
            </div>
          </div>
          {p.rating != null && <span className="flex items-center gap-1 text-amber-400 font-semibold"><Star className="w-5 h-5 fill-amber-400" />{p.rating} <span className="text-zinc-500 text-sm">({p.reviews_count})</span></span>}
        </div>
        {p.bio && <p className="text-sm text-zinc-400 mt-4 whitespace-pre-line">{p.bio}</p>}
        <div className="flex flex-wrap gap-1.5 mt-4">
          {(p.categories || []).map((c) => <span key={c} className="text-xs px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300">{c}</span>)}
          {(p.specialties || []).map((s) => <span key={s} className="text-xs px-2 py-1 rounded-full bg-zinc-900 border border-zinc-700 text-zinc-400">{s}</span>)}
        </div>
      </div>

      {(p.services || []).length > 0 && (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="pro-services">
          <h2 className="font-display text-lg font-bold mb-3">Services</h2>
          <div className="space-y-2">
            {p.services.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-3 bg-[#0F0F13] border border-zinc-800 rounded-xl p-3">
                <div className="min-w-0"><p className="font-medium text-zinc-200 truncate">{s.title}</p>{s.description && <p className="text-xs text-zinc-500 truncate">{s.description}</p>}</div>
                {s.price != null && s.price !== "" && <span className="text-amber-400 font-semibold text-sm shrink-0">{s.price}{s.unit ? ` €/${s.unit}` : " €"}</span>}
              </div>
            ))}
          </div>
          {p.pricing_note && <p className="text-xs text-zinc-500 mt-3">{p.pricing_note}</p>}
        </div>
      )}

      {(p.portfolio || []).length > 0 && (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="pro-portfolio">
          <h2 className="font-display text-lg font-bold mb-3">Portfolio</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {p.portfolio.map((it) => (
              <div key={it.id} className="rounded-xl overflow-hidden border border-zinc-800">
                <AuthImage path={it.media_path} className="w-full h-32 object-cover" alt={it.caption} />
                {it.caption && <p className="text-xs text-zinc-500 p-2 truncate">{it.caption}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="pro-reviews">
        <h2 className="font-display text-lg font-bold mb-3">Avis</h2>
        <div className="flex items-center gap-2 mb-3">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`}><Star className={`w-6 h-6 ${n <= rating ? "text-amber-400 fill-amber-400" : "text-zinc-600"}`} /></button>
          ))}
        </div>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} data-testid="review-comment" placeholder="Partage ton expérience…"
          className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2 text-sm text-zinc-100 outline-none focus:border-amber-500/50 resize-none" />
        <button onClick={submitReview} data-testid="review-submit" className="mt-2 h-10 px-4 rounded-xl gold-gradient text-black font-semibold text-sm">Publier mon avis</button>
        <div className="mt-4 space-y-3">
          {(p.reviews || []).length === 0 ? <p className="text-sm text-zinc-500">Aucun avis pour le moment.</p> :
            p.reviews.map((r) => (
              <div key={r.id} className="border-t border-zinc-800 pt-3">
                <div className="flex items-center gap-2"><span className="font-medium text-sm text-zinc-200">{r.author_name || "Membre"}</span>
                  <span className="flex items-center text-amber-400 text-xs">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3 h-3 fill-amber-400" />)}</span></div>
                {r.comment && <p className="text-sm text-zinc-400 mt-1">{r.comment}</p>}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
